import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Stack,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Download as ExportIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  CheckCircle as CompleteIcon,
  Warning as IncompleteIcon,
  Person as ClientIcon,
  Business as DomainIcon,
  Schedule as TimeIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Code as CodeIcon,
  Visibility as VisibilityIcon,
  Assignment as AssignmentIcon,
  CheckCircleOutline as ValidIcon,
  ErrorOutline as InvalidIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

// Usar API diretamente como na versão original
import { API_BASE_URL } from '../constants';

const DataCollectionTab = ({ agentId, agentName }) => {
  // Estados para dados das sessões
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [domains, setDomains] = useState([]);

  // Estados para filtros (mesmos da versão original)
  const [filters, setFilters] = useState({
    agent_id: agentId, // Já filtrado pelo agente
    domain: '',
    client_name: '',
    validation_status: '',
    start_date: '',
    end_date: '',
  });

  // Estados para paginação (mesmo formato da versão original)
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalSessions, setTotalSessions] = useState(0);

  // Estados para estatísticas (mesmo formato da versão original)
  const [stats, setStats] = useState({});

  // Estado para modal de detalhes (mesmo nome da versão original)
  const [selectedSession, setSelectedSession] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [showJsonView, setShowJsonView] = useState(false);

  // Carregar domínios disponíveis (mesmo da versão original)
  const loadDomains = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/data-collection/domains`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const domainsList = await response.json();
        setDomains(domainsList);
      } else {
        console.warn('Erro ao carregar domínios, usando valores padrão');
        setDomains([]);
      }
    } catch (error) {
      console.warn('Erro ao carregar domínios:', error);
      setDomains([]);
    }
  };

  // Função para carregar dados das sessões (mesma da versão original)
  const loadSessions = async (page = 1) => {
    if (!agentId) return;

    try {
      setLoading(true);
      setError(null);

      // Construir query string (mesmo formato da versão original)
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString()
      });

      // Adicionar filtros se preenchidos
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          params.append(key, value);
        }
      });

      
      const response = await fetch(`${API_BASE_URL}/data-collection/sessions?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao carregar sessões');
      }

      const result = await response.json();
      
      setSessions(result.sessions || []);
      setStats(result.stats || {});
      setCurrentPage(page);
      setTotalSessions(result.pagination?.total_records || result.stats?.total_sessions || result.sessions?.length || 0);
      
    } catch (err) {
      console.error('❌ [DATA_COLLECTION_TAB] Erro ao carregar sessões:', err);
      setError('Erro ao carregar sessões: ' + err.message);
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  };

  // Carregar dados quando a aba for acessada pela primeira vez (mesmo da versão original)
  useEffect(() => {
    if (!initialized) {
      loadDomains(); // Carregar domínios primeiro
      loadSessions();
    }
  }, [agentId, initialized]);

  // useEffect para recarregar quando filtros mudam (mesmo da versão original)
  useEffect(() => {
    if (domains.length > 0 && initialized) { // Só recarrega se já carregou os domínios
      const timeoutId = setTimeout(() => {
        setCurrentPage(1); // Reset para primeira página
        loadSessions(1);
      }, 300); // Debounce de 300ms para evitar muitas chamadas

      return () => clearTimeout(timeoutId);
    }
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  // useEffect separado para mudanças de página (mesmo da versão original)
  useEffect(() => {
    if (domains.length > 0 && initialized) {
      loadSessions(currentPage);
    }
  }, [currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Aplicar filtros (mesmo da versão original)
  const applyFilters = () => {
    setCurrentPage(1);
    loadSessions(1);
  };

  // Limpar filtros (mesmo da versão original)
  const clearFilters = () => {
    setFilters({
      agent_id: agentId, // Manter filtro do agente
      domain: '',
      client_name: '',
      validation_status: '',
      start_date: '',
      end_date: ''
    });
    setCurrentPage(1);
    loadSessions(1);
  };

  // Abrir modal de detalhes (mesmo da versão original)
  const openDetailModal = (session) => {
    setSelectedSession(session);
    setDetailModalOpen(true);
  };

  // Fechar modal de detalhes (mesmo da versão original)
  const closeDetailModal = () => {
    setSelectedSession(null);
    setDetailModalOpen(false);
    setShowJsonView(false); // Reset da visualização JSON
  };

  // Função para renderizar dados estruturados de forma visual
  const renderStructuredData = (data) => {
    if (!data || typeof data !== 'object') return 'N/A';

    return (
      <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
        {Object.entries(data).map(([key, value], index) => {
          // Pular campos técnicos
          if (['validation_status', 'collected_at'].includes(key)) return null;
          
          return (
            <Box 
              key={index} 
              sx={{ 
                display: 'flex', 
                alignItems: 'flex-start',
                py: 1.5,
                borderBottom: index < Object.entries(data).length - 1 ? '1px solid' : 'none',
                borderColor: 'grey.200',
                '&:last-child': { borderBottom: 'none' }
              }}
            >
              <InfoIcon fontSize="small" color="primary" sx={{ mr: 2, mt: 0.5, flexShrink: 0 }} />
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography 
                  variant="subtitle2" 
                  color="text.secondary" 
                  sx={{ 
                    fontWeight: 500, 
                    textTransform: 'capitalize',
                    fontSize: '0.875rem',
                    mb: 0.5
                  }}
                >
                  {key.replace(/_/g, ' ')}
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontWeight: 500,
                    wordBreak: 'break-word',
                    color: 'text.primary'
                  }}
                >
                  {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  // Função para obter cor do status de validação
  const getValidationColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'valid': return 'success';
      case 'invalid': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  // Função para obter ícone do status de validação
  const getValidationIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'valid': return <ValidIcon />;
      case 'invalid': return <InvalidIcon />;
      default: return <InfoIcon />;
    }
  };

  // Formatar data (mesmo da versão original)
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  // Formatação de domínio (mesmo da versão original)
  const formatDomain = (domain) => {
    // Buscar no mapeamento dinâmico primeiro
    const domainObj = domains.find(d => d.value === domain);
    if (domainObj) {
      return domainObj.label;
    }
    
    // Fallback para casos não mapeados
    const fallbackMap = {
      'unknown': 'Não identificado',
      'juridico': 'Jurídico',
      'vendas': 'Vendas',
      'rh': 'Recursos Humanos',
      'suporte': 'Suporte'
    };
    
    return fallbackMap[domain] || domain || 'Não identificado';
  };

  // Obter cor do status (mesmo da versão original)
  const getStatusColor = (status) => {
    switch (status) {
      case 'complete': return 'success';
      case 'incomplete': return 'warning';
      case 'in_progress': return 'info';
      default: return 'default';
    }
  };

  // Obter texto do status (mesmo da versão original)
  const getStatusText = (status) => {
    switch (status) {
      case 'complete': return 'Completa';
      case 'incomplete': return 'Incompleta';
      case 'in_progress': return 'Em Andamento';
      default: return status;
    }
  };

  // Função para visualizar detalhes da sessão (usar a função já definida)
  const handleViewDetails = (session) => {
    openDetailModal(session);
  };

  // Função para exportar sessões
  const handleExport = async () => {
    try {
      // Implementar exportação se necessário
      console.log('🔄 Exportando sessões...');
      // TODO: Implementar exportação
    } catch (err) {
      console.error('Erro ao exportar:', err);
    }
  };

  if (!initialized && loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Box sx={{ p: 0 }}>
        {/* Header com título e ações */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 3 
        }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              Sessões de Coleta de Dados
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Visualize e gerencie sessões completas de coleta de dados para {agentName}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Atualizar">
              <IconButton onClick={() => loadSessions()} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined"
              startIcon={<ExportIcon />}
              onClick={handleExport}
              size="small"
            >
              Exportar
            </Button>
          </Stack>
        </Box>

        {/* Estatísticas (mesmo formato da versão original) */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="primary" sx={{ fontWeight: 600 }}>
                  {stats.total_sessions || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total de Sessões
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="success.main" sx={{ fontWeight: 600 }}>
                  {stats.complete_sessions || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Sessões Completas
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="warning.main" sx={{ fontWeight: 600 }}>
                  {stats.incomplete_sessions || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Incompletas
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="info.main" sx={{ fontWeight: 600 }}>
                  {stats.unique_clients || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Clientes Únicos
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filtros (mesmo formato da versão original com largura fixa) */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <FilterIcon sx={{ mr: 1, verticalAlign: 'middle' }} />Filtros
            </Typography>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {/* Filtros com largura fixa forçada */}
              <Box sx={{ minWidth: '200px', flex: '1 1 200px' }}>
                <TextField
                  fullWidth
                  label="Domínio"
                  select
                  size="small"
                  variant="outlined"
                  value={filters.domain}
                  onChange={(e) => setFilters({...filters, domain: e.target.value})}
                  sx={{ minWidth: '200px' }}
                >
                  <MenuItem value="">Todos os Domínios</MenuItem>
                  {domains.map((domain) => (
                    <MenuItem key={domain.value} value={domain.value}>
                      {domain.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              
              <Box sx={{ minWidth: '200px', flex: '1 1 200px' }}>
                <TextField
                  fullWidth
                  label="Status da Sessão"
                  select
                  size="small"
                  variant="outlined"
                  value={filters.validation_status}
                  onChange={(e) => setFilters({...filters, validation_status: e.target.value})}
                  sx={{ minWidth: '200px' }}
                >
                  <MenuItem value="">Todos os Status</MenuItem>
                  <MenuItem value="complete">Completa</MenuItem>
                  <MenuItem value="incomplete">Incompleta</MenuItem>
                  <MenuItem value="in_progress">Em Andamento</MenuItem>
                </TextField>
              </Box>

              <Box sx={{ minWidth: '200px', flex: '1 1 200px' }}>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  label="Cliente"
                  value={filters.client_name}
                  onChange={(e) => setFilters({...filters, client_name: e.target.value})}
                  placeholder="Nome do cliente"
                  sx={{ minWidth: '200px' }}
                />
              </Box>

              <Box sx={{ minWidth: '200px', flex: '1 1 200px', display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={applyFilters}
                  disabled={loading}
                  size="small"
                  sx={{ minWidth: 80 }}
                >
                  Filtrar
                </Button>
                <Button
                  variant="outlined"
                  onClick={clearFilters}
                  disabled={loading}
                  size="small"
                  startIcon={<ClearIcon />}
                  sx={{ minWidth: 80 }}
                >
                  Limpar
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Mensagens de erro */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Tabela de sessões */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Data/Hora</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell>Agente</TableCell>
                <TableCell>Domínio</TableCell>
                <TableCell>Templates</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : sessions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      Nenhuma sessão encontrada
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sessions.map((session, index) => (
                  <TableRow key={session.session_key || index} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <TimeIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                        {formatDate(session.start_time)}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ClientIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                        {session.client_name || 'N/A'}
                      </Box>
                    </TableCell>
                    <TableCell>{session.agent_name}</TableCell>
                    <TableCell>
                      <Chip
                        label={formatDomain(session.domain)}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {session.templates && Array.isArray(session.templates) ? (
                        <Typography variant="body2">
                          {session.templates.length} templates
                        </Typography>
                      ) : (
                        'N/A'
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={session.status === 'complete' ? <CompleteIcon /> : <IncompleteIcon />}
                        label={getStatusText(session.status)}
                        color={getStatusColor(session.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Ver Detalhes">
                        <IconButton
                          size="small"
                          onClick={() => handleViewDetails(session)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          {/* Paginação simples como na versão original */}
          {totalSessions > pageSize && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1 || loading}
                sx={{ mr: 2 }}
              >
                Anterior
              </Button>
              <Typography sx={{ alignSelf: 'center', mx: 2 }}>
                Página {currentPage}
              </Typography>
              <Button
                variant="outlined"
                onClick={() => setCurrentPage(prev => prev + 1)}
                disabled={sessions.length < pageSize || loading}
              >
                Próxima
              </Button>
            </Box>
          )}
        </TableContainer>

        {/* Modal de detalhes da sessão - Interface Visual Melhorada */}
        <Dialog
          open={detailModalOpen}
          onClose={closeDetailModal}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AssignmentIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Detalhes da Sessão</Typography>
              </Box>
              <IconButton onClick={closeDetailModal} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            {selectedSession && (
              <Box>
                {/* Header com informações básicas simplificado */}
                <Box sx={{ p: 3, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <ClientIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Cliente
                        </Typography>
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 500 }}>
                        {selectedSession.client_name || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <TimeIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Data e Hora da Sessão
                        </Typography>
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 500 }}>
                        {formatDate(selectedSession.start_time)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <DomainIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Domínio
                        </Typography>
                      </Box>
                      <Chip
                        label={formatDomain(selectedSession.domain)}
                        color="primary"
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Status da Sessão
                        </Typography>
                      </Box>
                      <Chip
                        icon={selectedSession.status === 'complete' ? <CompleteIcon /> : <IncompleteIcon />}
                        label={getStatusText(selectedSession.status)}
                        color={getStatusColor(selectedSession.status)}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <AssignmentIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Templates Coletados
                        </Typography>
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main' }}>
                        {selectedSession.templates?.length || 0}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                {/* Tabs para diferentes visualizações */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs
                    value={showJsonView ? 1 : 0}
                    onChange={(e, newValue) => setShowJsonView(newValue === 1)}
                  >
                    <Tab
                      icon={<VisibilityIcon />}
                      iconPosition="start"
                      label="Dados Organizados"
                    />
                    <Tab
                      icon={<CodeIcon />}
                      iconPosition="start"
                      label="JSON Completo"
                    />
                  </Tabs>
                </Box>

                {/* Conteúdo das tabs */}
                <Box sx={{ p: 3 }}>
                  {!showJsonView ? (
                    // Visualização organizada por templates
                    <Box>
                      {selectedSession.templates && Array.isArray(selectedSession.templates) ? (
                        selectedSession.templates.map((template, index) => (
                          <Accordion key={index} defaultExpanded={index === 0} sx={{ mb: 2 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                <AssignmentIcon sx={{ mr: 2, color: 'primary.main' }} />
                                <Box sx={{ flexGrow: 1 }}>
                                  <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                    {template.display_name || template.template_name}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    Coletado em: {formatDate(template.collected_at)}
                                  </Typography>
                                </Box>
                                <Chip
                                  icon={getValidationIcon(template.validation_status)}
                                  label={template.validation_status || 'N/A'}
                                  color={getValidationColor(template.validation_status)}
                                  size="small"
                                />
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 2 }}>
                              <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                  <AssignmentIcon sx={{ mr: 1, color: 'primary.main' }} />
                                  <Typography variant="subtitle1" sx={{ fontWeight: 500, color: 'primary.main' }}>
                                    Dados Coletados
                                  </Typography>
                                </Box>
                                {renderStructuredData(template.structured_data)}
                              </Box>
                            </AccordionDetails>
                          </Accordion>
                        ))
                      ) : (
                        <Alert severity="info">
                          Nenhum template encontrado para esta sessão.
                        </Alert>
                      )}
                    </Box>
                  ) : (
                    // Visualização JSON completa
                    <Box>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        JSON Completo da Sessão
                      </Typography>
                      <Paper sx={{ p: 2, bgcolor: 'grey.900', color: 'grey.100', maxHeight: 400, overflow: 'auto' }}>
                        <pre style={{ margin: 0, fontSize: '0.875rem', whiteSpace: 'pre-wrap' }}>
                          {JSON.stringify(selectedSession, null, 2)}
                        </pre>
                      </Paper>
                    </Box>
                  )}
                </Box>

                {/* Session Key técnica no rodapé */}
                <Divider />
                <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="caption" color="text.secondary">
                    Session Key: {selectedSession.session_key}
                  </Typography>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={closeDetailModal} variant="outlined" startIcon={<CloseIcon />}>
              Fechar
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </motion.div>
  );
};

export default DataCollectionTab;
